import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  AlertCircle,
  Tag,
  Zap,
  Brain,
  GitBranch,
  Box,
  ArrowLeftRight,
} from "lucide-react";

type AiWorkflowType = 'quick_reasoning' | 'deep_reasoning' | 'chain_of_thought' | 'object_detection' | 'change_detection';

const WORKFLOW_MAP: Record<AiWorkflowType, { label: string; icon: React.ElementType }> = {
  quick_reasoning: { label: 'Quick Reasoning', icon: Zap },
  deep_reasoning: { label: 'Deep Reasoning', icon: Brain },
  chain_of_thought: { label: 'Chain of Thought', icon: GitBranch },
  object_detection: { label: 'Object Detection', icon: Box },
  change_detection: { label: 'Change Detection', icon: ArrowLeftRight },
};

interface AIAnalysisDialogProps {
  device: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AIAnalysisDialog = ({ device, open, onOpenChange }: AIAnalysisDialogProps) => {
  const hasNotification = device.notification?.result?.toLowerCase() === "true";
  const subject = (device.notification as any)?.subject;
  const message = device.notification?.message;
  const reason = (device.notification as any)?.reason;
  const notificationData = device.notification_data || (device as any).notificationData;
  const aiWorkflow: AiWorkflowType | undefined = (device as any).ai_workflow || (device as any).aiWorkflow;

  const workflowInfo = aiWorkflow ? WORKFLOW_MAP[aiWorkflow] : null;
  const WorkflowIcon = workflowInfo?.icon;

  // Parse notification data for badges
  const dataEntries: [string, unknown][] = (() => {
    if (!notificationData) return [];

    // Coerce JSON-string payloads into objects
    let value: unknown = notificationData;
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch {
        return [];
      }
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const entries = Object.entries(value as Record<string, unknown>);
      // Reject char-indexed corrupt shapes like {"0":"{","1":"\"","2":"i",...}
      const looksCharIndexed =
        entries.length > 0 &&
        entries.every(([k, v], i) => k === String(i) && typeof v === 'string' && (v as string).length === 1);
      if (looksCharIndexed) return [];
      return entries;
    }
    return [];
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="ai-analysis-dialog max-w-2xl max-h-[80vh] overflow-y-auto p-0 gap-0"
        onClick={(e) => e.stopPropagation()}
        onPointerDownOutside={(e) => e.stopPropagation()}
      >
        {/* Colored accent header */}
        <div className={`ai-analysis-header px-6 pt-6 pb-4 ${
          hasNotification 
            ? 'bg-gradient-to-r from-red-50 to-red-100/50 border-b border-red-200' 
            : 'bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200'
        }`}>
          <DialogHeader className="space-y-1">
            <div className="flex items-center gap-2">
              <DialogTitle className="text-lg">AI Analysis</DialogTitle>
              {workflowInfo && WorkflowIcon && (
                <Badge variant="secondary" className="gap-1 text-[10px] font-medium">
                  <WorkflowIcon className="h-3 w-3" />
                  {workflowInfo.label}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{device.deviceName}</p>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* 1. Status banner */}
          <div className={`ai-analysis-banner flex items-center gap-3 px-4 py-3 rounded-lg ${
            hasNotification
              ? 'bg-red-50 text-red-800 border border-red-200'
              : 'bg-green-50 text-green-800 border border-green-200'
          }`}>
            {hasNotification 
              ? <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
              : <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
            }
            <span className="text-sm font-semibold">
              {hasNotification ? "Alert Triggered" : "No Alert"}
            </span>
          </div>

          {/* 2. Subject */}
          {subject && (
            <div>
              <h3 className="text-base font-bold text-foreground">{subject}</h3>
            </div>
          )}

          {/* 3. Analysis */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b border-border">
              <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Analysis</span>
            </div>
            <p className="text-sm text-foreground px-4 py-3 whitespace-pre-wrap leading-relaxed">
              {message || "No recent AI analysis available"}
            </p>
          </div>

          {/* 4. Reason */}
          {reason && (
            <div className="ai-analysis-reason rounded-lg border-l-4 border-l-amber-400 border border-border bg-amber-50/30">
              <div className="flex items-center gap-2 px-4 py-2">
                <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">Reason</span>
              </div>
              <p className="text-sm text-foreground px-4 pb-3">
                {reason}
              </p>
            </div>
          )}

          {/* 5. AI Query */}
          {(device as any).notification_question && (
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b border-border">
                <Zap className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">AI Query</span>
              </div>
              <p className="text-sm text-foreground px-4 py-3">
                {(device as any).notification_question}
              </p>
            </div>
          )}

          {/* 6. Data Tags */}
          {dataEntries.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Data Tags</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {dataEntries.map(([key, value]) => (
                  <Badge key={key} variant="secondary" className="text-[11px] gap-1 py-1">
                    <span className="font-semibold">{key}:</span>
                    <span className="font-normal">{String(value)}</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
